import { ClientMessage, ServerMessage, ServerMessageType } from '@pvp/shared';

type MessageHandler = (msg: ServerMessage) => void;
type LifecycleHandler = () => void;

export class NetworkManager {
  private ws: WebSocket | null = null;
  private handlers: Map<ServerMessageType, MessageHandler[]> = new Map();
  private openHandlers: LifecycleHandler[] = [];
  private closeHandlers: LifecycleHandler[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private manualClose = false;

  connect(): void {
    if (this.ws && (
      this.ws.readyState === WebSocket.OPEN
      || this.ws.readyState === WebSocket.CONNECTING
    )) {
      return;
    }

    this.manualClose = false;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${location.host}/ws`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log('Connected to server');
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      for (const handler of this.openHandlers) {
        handler();
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as ServerMessage;
        const handlers = this.handlers.get(msg.type) || [];
        for (const handler of handlers) {
          handler(msg);
        }
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    this.ws.onclose = () => {
      console.log('Disconnected');
      this.ws = null;
      for (const handler of this.closeHandlers) {
        handler();
      }
      if (!this.manualClose) {
        this.reconnectTimer = setTimeout(() => this.connect(), 3000);
      }
    };

    this.ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  on(type: ServerMessageType, handler: MessageHandler): void {
    const list = this.handlers.get(type) || [];
    list.push(handler);
    this.handlers.set(type, list);
  }

  onOpen(handler: LifecycleHandler): void {
    this.openHandlers.push(handler);
  }

  onClose(handler: LifecycleHandler): void {
    this.closeHandlers.push(handler);
  }

  disconnect(): void {
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
